// Section14.jsx — Our Mobile App Development Process
import React from 'react';
import { useReveal } from '../../componets/useScrollFX';

const steps = [
  {
    num: "01",
    title: "Discovery",
    intro: "We begin by understanding your:",
    points: ["Business objectives", "Target audience", "Technical requirements", "Competitive landscape", "Growth goals"],
    outro: "This foundation guides every stage of development.",
  },
  {
    num: "02",
    title: "UI/UX Design",
    intro: "Our designers create engaging user experiences through:",
    points: ["User research", "Wireframes", "User flows", "Interactive prototypes", "Visual design systems"],
    outro: "The focus is on usability, accessibility, and engagement.",
  },
  {
    num: "03",
    title: "Development",
    intro: "Our developers build your application using modern technologies and industry best practices. This stage includes:",
    points: ["Frontend development", "Backend development", "API integrations", "Database architecture", "Feature implementation"],
    outro: null,
  },
  {
    num: "04",
    title: "Testing",
    intro: "Quality assurance is integrated throughout the project. We perform:",
    points: ["Functional testing", "Performance testing", "Security testing", "Device testing", "User acceptance testing"],
    outro: "This ensures a stable and reliable application.",
  },
  {
    num: "05",
    title: "Deployment",
    intro: "Once approved, we prepare your application for launch. Deployment includes:",
    points: ["Store submission support", "Performance optimization", "Release management", "Final validation"],
    outro: null,
  },
  {
    num: "06",
    title: "Support",
    intro: "Our relationship continues after launch. We provide:",
    points: ["Maintenance", "Monitoring", "Security updates", "Performance improvements", "Feature enhancements", "Technical support"],
    outro: "This helps your application remain competitive and effective over time.",
  },
];

const StepCard = ({ step, index }) => {
  const [ref, visible] = useReveal(0.1);
  return (
    <div
      ref={ref}
      className={`flex flex-col sm:flex-row gap-4 sm:gap-8 mb-5 sm:mb-6 items-stretch sm:items-start last:mb-0 group relative transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      {/* Icon: inline with title on mobile, floating node on desktop */}
      <div className="hidden sm:flex flex-shrink-0 w-14 h-14 rounded-2xl bg-[#cd2122] text-white items-center justify-center text-lg font-bold shadow-lg z-10 group-hover:scale-105 transition-transform duration-300">
        {step.num}
      </div>

      <div className="flex-1 bg-white rounded-2xl p-5 sm:p-7 shadow-sm border border-gray-100 hover:border-[#cd2122]/30 hover:shadow-lg transition-all duration-300">
        <div className="flex items-center gap-3 mb-3">
          <span className="sm:hidden flex-shrink-0 w-9 h-9 rounded-xl bg-[#cd2122] text-white flex items-center justify-center text-sm font-bold">
            {step.num}
          </span>
          <h3 className="text-lg sm:text-xl font-bold text-[#1a1a1a]">{step.title}</h3>
        </div>
        <p className="text-[#535353] text-[15px] sm:text-[16px] leading-relaxed mb-4">{step.intro}</p>

        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-4">
          {step.points.map((point, i) => (
            <li key={i} className="flex items-center gap-2.5 text-[14px] sm:text-[15px] text-[#535353]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#cd2122] flex-shrink-0" />
              {point}
            </li>
          ))}
        </ul>

        {step.outro && (
          <p className="text-[14px] sm:text-[15px] text-[#1a1a1a]/70 italic pt-4 border-t border-gray-100">{step.outro}</p>
        )}
      </div>
    </div>
  );
};

const Section14 = () => {
  const [headRef, headVisible] = useReveal();

  return (
    <section id="process" className="bg-[#fafafa] py-10 md:py-10">
      <div className="max-w-7xl mx-auto px-6">
        <div
          ref={headRef}
          className={`text-center mb-16 transition-all duration-700 ${
            headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="text-[#cd2122] text-xs font-semibold uppercase tracking-widest">Our Process</span>
          <h2 className="text-[32px] md:text-[38px] font-extrabold text-[#1a1a1a] mt-3 mb-4 leading-tight">
            Our Mobile App Development Process
          </h2>
          <p className="text-[#535353] text-[16px] max-w-xl mx-auto">
            A structured development process helps ensure project success, transparency, and predictable outcomes.
          </p>
        </div>

        <div className="max-w-6xl mx-auto relative">
          {/* Vertical Line */}
          <div className="hidden md:block absolute left-7 top-8 bottom-8 w-px bg-gray-200" />

          {steps.map((step, index) => (
            <StepCard key={index} step={step} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Section14;